/**
 * @fileoverview Popover form for entering BaseLinker API credentials.
 * @module features/settings/components/BaselinkerCredentialsPopover
 */

import { type FormEvent, type ReactElement, useRef, useState } from 'react'
import { Loader2, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import { Popover, PopoverTrigger } from '@/components/ui/popover'
import { ViewportAwarePopoverContent } from '@/shared/components/ui/ViewportAwarePopoverContent'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { OrgIdSchema } from '@/features/orgs/schemas/orgSchemas'
import apiClient from '@/lib/apiClient'
import { useAdPlatformSyncStatus } from '../hooks/useAdPlatformSyncStatus'

/**
 * Zod schema for BaseLinker credentials form input.
 */
const BaseLinkerCredentialsSchema = z.object({
  blApiToken: z.string().min(1, 'API token is required'),
})

export interface BaselinkerCredentialsPopoverProps {
  /** Callback after credentials are saved successfully */
  onSaved?: () => Promise<unknown> | unknown
}

/**
 * BaselinkerCredentialsPopover
 *
 * Popover form for entering BaseLinker API token credentials.
 * Styled similarly to ProductEditPopover for consistency.
 *
 * @component
 */
export function BaselinkerCredentialsPopover({
  onSaved,
}: BaselinkerCredentialsPopoverProps): ReactElement {
  const { t } = useTranslation('features-integrations')
  const { activeOrgId } = useActiveOrg()
  const queryClient = useQueryClient()
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const { isSyncing, markConnectionComplete } = useAdPlatformSyncStatus('BASELINKER')

  const [open, setOpen] = useState(false)
  const [blApiToken, setBlApiToken] = useState('')

  const mutation = useMutation({
    mutationFn: async (): Promise<void> => {
      if (!activeOrgId) throw new Error('No active organization')
      const orgId = OrgIdSchema.parse(activeOrgId) as unknown as string
      const payload = BaseLinkerCredentialsSchema.parse({ blApiToken })
      await apiClient.put(`/orgs/${orgId}/integrations/BASELINKER/credentials`, payload)
    },
    onSuccess: async () => {
      toast.success(
        t('cards.integrations.baselinker.saved', { defaultValue: 'BaseLinker credentials saved' })
      )

      // Mark connection complete to start polling for sync status
      markConnectionComplete()

      // Show sync started toast
      toast.info(
        t('cards.integrations.baselinker.syncStarted', {
          defaultValue: 'Syncing your order data... This may take a few minutes.',
        })
      )

      setOpen(false)
      setBlApiToken('')
      // Invalidate integrations status to reflect the new connection
      await queryClient.invalidateQueries({ queryKey: ['integrations', 'status'] })
      await onSaved?.()
    },
    onError: (e: unknown) => {
      toast.error(
        (e as Error)?.message ??
          t('cards.integrations.baselinker.error', {
            defaultValue: 'Failed to save BaseLinker credentials',
          })
      )
    },
  })

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    if (blApiToken.trim().length === 0) return
    mutation.mutate()
  }

  const disabled = !activeOrgId || mutation.isPending || blApiToken.trim().length === 0

  return (
    <>
      <Popover open={false}>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setOpen(true)}
            disabled={isSyncing}
            className="gap-1.5"
          >
            {isSyncing ? (
              <>
                <Loader2 className="size-3.5 animate-spin" />
                {t('cards.integrations.baselinker.buttons.syncing', { defaultValue: 'Syncing...' })}
              </>
            ) : (
              <>
                <Plus className="size-3.5" />
                {t('cards.integrations.baselinker.buttons.connect', { defaultValue: 'Connect' })}
              </>
            )}
          </Button>
        </PopoverTrigger>
      </Popover>

      <ViewportAwarePopoverContent
        open={open}
        triggerRef={triggerRef}
        width={320}
        estimatedHeight={200}
        className="w-80 space-y-4 bg-popover"
        onClose={() => {
          if (!mutation.isPending) {
            setOpen(false)
          }
        }}
      >
        <form className="space-y-4" onSubmit={handleSubmit}>
          <header className="space-y-1">
            <p className="text-sm font-semibold text-foreground">
              {t('cards.integrations.baselinker.title', { defaultValue: 'Connect BaseLinker' })}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('cards.integrations.baselinker.description', {
                defaultValue: 'Enter your BaseLinker API token to sync orders.',
              })}
            </p>
          </header>

          <div className="grid gap-1">
            <Label
              htmlFor="bl-api-token-popover"
              className="text-xs font-medium text-muted-foreground"
            >
              {t('cards.integrations.baselinker.fields.apiToken', { defaultValue: 'API Token' })}
            </Label>
            <Input
              id="bl-api-token-popover"
              type="password"
              value={blApiToken}
              onChange={e => setBlApiToken(e.target.value)}
              placeholder={t('cards.integrations.baselinker.placeholders.apiToken', {
                defaultValue: 'Enter BaseLinker API token',
              })}
              autoComplete="off"
              required
            />
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={disabled}>
              {mutation.isPending
                ? t('cards.integrations.baselinker.buttons.saving', { defaultValue: 'Saving…' })
                : t('cards.integrations.baselinker.buttons.save', { defaultValue: 'Save' })}
            </Button>
          </div>
        </form>
      </ViewportAwarePopoverContent>
    </>
  )
}

export default BaselinkerCredentialsPopover
